import React, { useState, useEffect } from "react";
import { withApollo } from "react-apollo";
import { useHistory, useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import IconButton from "@material-ui/core/IconButton";
import Snackbar from "@material-ui/core/Snackbar";
import Alert from "@material-ui/lab/Alert";
import gql from "graphql-tag";
import { API_URL } from "../../config";

const GET_CVD_BY_TAG = gql`
  query GetCvdByTag($tagNo: String!) {
    allDiamondComparisons(condition: { tagNo: $tagNo }) {
      nodes {
        tagNo
        name
        naturalDiamondMetalWeight
        naturalDiamondPurity
        naturalDiamondMetalValue
        naturalDiamondDiamondType
        naturalDiamondNoOfDiamonds
        naturalDiamondDiamondCarat
        naturalDiamondDiamondPrice
        naturalDiamondCertificate
        naturalDiamondTotalPrice
        metalWeight
        purity
        metalValue
        diamondType
        noOfDiamonds
        diamondCarat
        diamondPrice
        certificate
        totalPrice
        totalSaved
        diamondInProductValue
        makingCharge
        gstPercentage
        gstValue
        metalPrice
        addOnPercentage
      }
    }
  }
`;

const UPDATE_CVD = gql`
  mutation UpdateDiamondComparison($tagNo: String!, $input: DiamondComparisonPatch!) {
    updateDiamondComparisonByTagNo(input: { tagNo: $tagNo, diamondComparisonPatch: $input }) {
      diamondComparison {
        tagNo
      }
    }
  }
`;

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  header: { display: "flex", alignItems: "center", marginBottom: theme.spacing(3) },
  title: { marginLeft: theme.spacing(1) },
  section: { marginBottom: theme.spacing(3), padding: theme.spacing(2) },
  sectionTitle: { marginBottom: theme.spacing(2), fontWeight: "bold", color: "#1976d2" },
  actions: { marginTop: theme.spacing(3), display: "flex", gap: theme.spacing(2) },
}));

const fields = [
  { section: "Basic Info", fields: [
    { key: "tagNo", label: "Tag No", disabled: true },
    { key: "name", label: "Name" },
  ]},
  { section: "Natural Diamond Details", fields: [
    { key: "naturalDiamondMetalWeight", label: "ND Metal Weight" },
    { key: "naturalDiamondPurity", label: "ND Purity" },
    { key: "naturalDiamondMetalValue", label: "ND Metal Value" },
    { key: "naturalDiamondDiamondType", label: "ND Diamond Type" },
    { key: "naturalDiamondNoOfDiamonds", label: "ND No of Diamonds" },
    { key: "naturalDiamondDiamondCarat", label: "ND Diamond Carat" },
    { key: "naturalDiamondDiamondPrice", label: "ND Diamond Price" },
    { key: "naturalDiamondCertificate", label: "ND Certificate" },
    { key: "naturalDiamondTotalPrice", label: "ND Total Price" },
  ]},
  { section: "CVD Diamond Details", fields: [
    { key: "metalWeight", label: "Metal Weight" },
    { key: "purity", label: "Purity" },
    { key: "metalValue", label: "Metal Value" },
    { key: "diamondType", label: "Diamond Type" },
    { key: "noOfDiamonds", label: "No of Diamonds" },
    { key: "diamondCarat", label: "Diamond Carat" },
    { key: "diamondPrice", label: "Diamond Price (per carat)" },
    { key: "certificate", label: "Certificate" },
    { key: "totalPrice", label: "Total Price" },
    { key: "totalSaved", label: "Total Saved" },
  ]},
  { section: "Additional Details", fields: [
    { key: "diamondInProductValue", label: "Diamond In Product Value" },
    { key: "makingCharge", label: "Making Charge" },
    { key: "gstPercentage", label: "GST%" },
    { key: "gstValue", label: "GST Value" },
    { key: "metalPrice", label: "Metal Price" },
    { key: "addOnPercentage", label: "Add On%" },
  ]},
];

export const CVDDetail = withApollo((props) => {
  const classes = useStyles();
  const history = useHistory();
  const { tagNo, mode } = useParams();
  const isView = mode === "view";
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ open: false, text: "", type: "success" });

  useEffect(() => {
    fetchRecord();
  }, [tagNo]);

  const fetchRecord = async () => {
    try {
      const result = await props.client.query({
        query: GET_CVD_BY_TAG,
        variables: { tagNo },
        fetchPolicy: "network-only",
      });
      const record = result.data.allDiamondComparisons.nodes[0];
      if (record) setFormData(record);
    } catch (error) {
      console.error("Error fetching record:", error);
    }
    setLoading(false);
  };

  const handleChange = (key, value) => {
    setFormData({ ...formData, [key]: value });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { __typename, createdat, updatedat, imageUrl1, imageUrl2, tagNo: t, ...updateFields } = formData;

      // Recalculate derived values
      const diamondCarat = parseFloat(updateFields.diamondCarat) || 0;
      const diamondPrice = parseFloat(updateFields.diamondPrice) || 0;
      const metalValue = parseFloat(updateFields.metalValue) || 0;
      const metalWeight = parseFloat(updateFields.metalWeight) || 0;
      const makingCharge = parseFloat(updateFields.makingCharge) || 0;
      const gstPercentage = parseFloat(updateFields.gstPercentage) || 0;
      const addOnPercentage = parseFloat(updateFields.addOnPercentage) || 0;
      const ndTotalPrice = parseFloat(updateFields.naturalDiamondTotalPrice) || 0;

      const diamondInProductValue = Math.round(diamondCarat * diamondPrice);
      const metalPriceCalc = Math.round(metalValue * metalWeight);
      const gstValue = Math.round((gstPercentage / 100) * (diamondInProductValue + makingCharge + metalPriceCalc));
      const totalPrice = Math.round((diamondInProductValue + makingCharge + metalPriceCalc + gstValue) * (1 + addOnPercentage / 100));
      const totalSaved = Math.round(ndTotalPrice - totalPrice);

      updateFields.diamondInProductValue = String(diamondInProductValue);
      updateFields.metalPrice = String(metalPriceCalc);
      updateFields.gstValue = String(gstValue);
      updateFields.totalPrice = String(totalPrice);
      updateFields.totalSaved = String(totalSaved);

      // Fetch cost_price from gold_price_settings for discount_price calculation
      let discountPrice = totalPrice;
      try {
        const goldResult = await props.client.query({
          query: gql`
            query {
              allGoldPriceSettings(condition: { material: "Gold", vendorCode: "STYPA 090" }, first: 50) {
                nodes {
                  purity
                  costPrice
                }
              }
            }
          `,
          fetchPolicy: "network-only",
        });
        const goldPrices = goldResult.data.allGoldPriceSettings.nodes;
        const purityNum = String(updateFields.purity).replace(/K/i, "");
        const found = goldPrices.find(g => String(g.purity) === purityNum);
        const metalCostValue = found ? parseFloat(found.costPrice) || 0 : 0;
        const metalPriceCost = Math.round(metalCostValue * metalWeight);
        const gstValueCost = Math.round((gstPercentage / 100) * (diamondInProductValue + makingCharge + metalPriceCost));
        discountPrice = Math.round((diamondInProductValue + makingCharge + metalPriceCost + gstValueCost) * (1 + addOnPercentage / 100));
      } catch (err) {
        console.error("Error fetching gold cost price:", err);
      }

      await props.client.mutate({
        mutation: UPDATE_CVD,
        variables: { tagNo, input: updateFields },
      });

      // Update trans_sku_lists markup_price and discount_price
      await fetch(`${API_URL}/updatecvdskuprice`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ productId: tagNo, totalPrice: totalPrice, discountPrice: discountPrice }),
      });

      setMessage({ open: true, text: "Saved successfully", type: "success" });
      setTimeout(() => history.push("/cvd"), 1000);
    } catch (error) {
      console.error("Error saving:", error);
      setMessage({ open: true, text: "Failed to save", type: "error" });
    }
    setSaving(false);
  };

  if (loading) {
    return <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><CircularProgress /></div>;
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <IconButton onClick={() => history.push("/cvd")}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" className={classes.title}>
          {isView ? "View" : "Edit"} CVD Record - {tagNo}
        </Typography>
      </div>

      {fields.map((section) => (
        <Paper key={section.section} className={classes.section} elevation={2}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            {section.section}
          </Typography>
          <Grid container spacing={2}>
            {section.fields.map((field) => (
              <Grid item xs={12} sm={6} md={4} key={field.key}>
                <TextField
                  label={field.label}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={formData[field.key] || ""}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  disabled={isView || field.disabled}
                  InputProps={{ readOnly: isView }}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      ))}

      {!isView && (
        <div className={classes.actions}>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
          <Button variant="outlined" onClick={() => history.push("/cvd")}>
            Cancel
          </Button>
        </div>
      )}

      <Snackbar open={message.open} autoHideDuration={3000} onClose={() => setMessage({ ...message, open: false })}>
        <Alert severity={message.type} onClose={() => setMessage({ ...message, open: false })}>
          {message.text}
        </Alert>
      </Snackbar>
    </div>
  );
});

export default CVDDetail;
